import React, { useEffect, useState } from 'react';
import { Upload } from 'antd';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { InboxOutlined } from '@ant-design/icons';
import WarningIcon from '@mui/icons-material/Warning';
import { CircularProgress } from '@mui/material';

import { updateSelfEmployedCourseModules } from '../../../store/courseService';
import { loadSelfEmployedManagementCourseModuleStatuses } from '../../../store/directusService';
import Button from '../../formElements/Button';

import '../../../commonStyles/antUploaderStyles.css';
import form from '../../../commonStyles/formStyles.module.css';
import modal from '../../../commonStyles/modal.module.css';
import uploader from '../../../commonStyles/uploader.module.css';
import local from './selfEmployed.module.css';

const { Dragger } = Upload;
export const FILE_TYPES = '.csv';
const _MAX_FILE_SIZE = 25;
const acceptedFileTypes = ['text/csv'];

export const FILE_SIZE_ERROR_MESSAGE = `Total file upload size must be smaller than ${_MAX_FILE_SIZE}MB`;
export const _FILE_SELECTION_ERROR_MESSAGE = `This file does not appear to have correct headers for extraction`;
export const _FILE_EXTRACTION_ERROR_MESSAGE = `This file does not contain any approved statuses for extraction`;

const SelfEmployedUploader = ({ isOpenUploader, onCancel, onExit }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [isPending, setIsPending] = useState(false);
    const [fileList, setFileList] = useState([]);
    const [totalQueueSize, setTotalQueueSize] = useState(0);
    const [wrongFileTypes, setWrongFileTypes] = useState([]);
    const [preExtractionErrors, setPreExtractionErrors] = useState({});
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [acceptExtraction, setAcceptExtraction] = useState(false);

    // STORE STATE
    const selfEmployedManagementCourseModuleStatuses = useSelector(
        (state) => state.entities.directusService.selfEmployedManagementCourseModuleStatuses
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // VENDOR FN
    const uploadProps = {
        name: 'file',
        onRemove(e) {
            const updatedList = fileList.filter((el) => el.uid !== e.uid);
            setFileList(updatedList);
            if (updatedList.length < 1) {
                setPreExtractionErrors({});
                setWrongFileTypes([]);
                setAcceptExtraction(false);
            }

            if (isValidSize(totalQueueSize - e.size)) clearError('fileSize');
            setTotalQueueSize((prev) => prev - e.size);
        },
        onDrop(e) {
            const allowedFiles = Object.values(e.dataTransfer.files).filter(
                (el) =>
                    acceptedFileTypes.includes(el.type) &&
                    !fileList.find((entry) => entry.name === el.name)
            );
            let wrongTypes = Object.values(e.dataTransfer.files).filter(
                (el) => !acceptedFileTypes.includes(el.type)
            );
            wrongTypes = [...wrongTypes, ...wrongFileTypes];
            const totalSize = allowedFiles.map((el) => el.size).reduce((acc, cur) => acc + cur, 0);
            setTotalQueueSize((prev) => prev + totalSize);

            !isValidSize(totalSize) && configFileError('fileSize', FILE_SIZE_ERROR_MESSAGE);
            wrongTypes.length > 0 && setWrongFileTypes(wrongTypes);
            allowedFiles.length > 0 && setFileList([...allowedFiles]);
            allowedFiles.length > 0 && isValidSize(totalSize) && setAcceptExtraction(true);
        },
        multiple: false,
        customRequest(info) {
            if (!acceptedFileTypes.includes(info.file.type)) {
                setWrongFileTypes([info.file, ...wrongFileTypes]);
            } else if (!fileList.find((entry) => entry.name === info.file.name)) {
                !isValidSize(info.file.size) &&
                    configFileError('fileSize', FILE_SIZE_ERROR_MESSAGE);
                setTotalQueueSize(info.file.size);
                setFileList([info.file]);
                isValidSize(info.file.size) && setAcceptExtraction(true);
            }
        }
    };

    // USEEFFECTS
    useEffect(() => {
        !selfEmployedManagementCourseModuleStatuses?.length &&
            dispatch(loadSelfEmployedManagementCourseModuleStatuses());
    }, []);

    useEffect(() => {
        const isErrors = Object.values(preExtractionErrors).some((el) => el.error);

        setIsButtonDisabled(isErrors);
    }, [preExtractionErrors]);

    useEffect(() => {
        if (!isOpenUploader) clearUploader();
    }, [isOpenUploader]);

    useEffect(() => {
        setIsPending(false);
        if (successMessage === 'Self employed course modules have been updated') clearUploader();
    }, [successMessage]);

    // HELPER FNS
    const configFileError = (key, message) => {
        setPreExtractionErrors((prev) => ({ ...prev, [key]: { error: true, message } }));
    };

    const isValidSize = (size) => size / 1024 / 1024 <= _MAX_FILE_SIZE;

    const clearError = (key) => {
        if (key in preExtractionErrors && preExtractionErrors[key].error)
            setPreExtractionErrors((prev) => ({ ...prev, [key]: { error: false } }));
    };

    // EVENT HANDLERS
    const onUpload = () => {
        if (fileList.length < 1) {
            onExit();
            return;
        }
        if (!isValidSize(totalQueueSize)) {
            configFileError('button', FILE_SIZE_ERROR_MESSAGE);
            return;
        }
        const file = fileList[0];
        const reader = new FileReader();

        if (file) {
            reader.readAsText(file);
        }

        reader.addEventListener('load', () => {
            const fileContents = reader.result.split('\n');

            if (
                fileContents[0] !==
                'Services,Trainer,Course Name,Self Employed Course Module Instance Id,Module Name,Module Date/Time,Current Payment Status,Current Payment Status Date,Price'
            ) {
                configFileError('button', _FILE_SELECTION_ERROR_MESSAGE);
                return;
            }

            const paidStatusId = selfEmployedManagementCourseModuleStatuses.find(
                (el) => el.name.toLowerCase() === 'paid'.toLowerCase()
            )?.id;
            const extractApproved = fileContents.filter((el) => el.includes('Approved'));

            const updateToPaidStatus = extractApproved.map((el) => {
                const rowArray = el.split(',');
                const moduleId = rowArray[3];
                return {
                    id: moduleId,
                    approvalStatusId: paidStatusId
                };
            });
            if (!updateToPaidStatus.length) {
                configFileError('button', _FILE_EXTRACTION_ERROR_MESSAGE);
            } else dispatch(updateSelfEmployedCourseModules({ updates: updateToPaidStatus }));
        });
    };

    const clearUploader = () => {
        setPreExtractionErrors({});
        setWrongFileTypes([]);
        setFileList([]);
        setTotalQueueSize(0);
        onExit();
    };

    // RENDER
    return (
        <div className={local.uploaderWrapper} data-testid="selfEmployed_uploader">
            <label htmlFor="uploader" className={form.formLabel}>
                Attach Documents
            </label>
            <div>
                {isPending && (
                    <div className={uploader.loadingSpinner}>
                        <CircularProgress />
                    </div>
                )}
                <Dragger
                    accept={FILE_TYPES}
                    fileList={fileList}
                    onDrop={(e) => uploadProps.onDrop(e)}
                    data-testid={`dragger`}
                    {...uploadProps}>
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                        Click or drag file to this area to extract Approved
                    </p>
                    <p className="ant-upload-hint">{`Support for fileType: ${FILE_TYPES}`}</p>
                </Dragger>
            </div>

            {wrongFileTypes.length > 0 &&
                (wrongFileTypes.length === 1 ? (
                    <div
                        className={uploader.uploaderFileWarning}
                        data-testid="wrong_file_type_warning">
                        <div className={uploader.warningText}>
                            <WarningIcon />
                            This file could not be extracted as it is the wrong type:{' '}
                            {wrongFileTypes[0].name}
                        </div>
                    </div>
                ) : (
                    <div className={uploader.uploaderFileWarning}>
                        <div className={uploader.warningText}>
                            <WarningIcon />
                            These files could not be extracted as they are the wrong type:
                        </div>
                        {wrongFileTypes.map((el) => (
                            <div key={el.name} className={uploader.warningRow}>
                                {el.name}
                            </div>
                        ))}
                    </div>
                ))}

            <div>
                <div className={modal.actionButtons}>
                    {acceptExtraction && (
                        <div className={local.customButtonMarginUploader}>
                            <Button
                                id="upload"
                                type="button"
                                content="Process Extraction"
                                disabled={isButtonDisabled}
                                customErrorClass="modalButtonError"
                                onClick={onUpload}
                                error={preExtractionErrors?.button}
                                clearError={() => clearError('button')}
                            />
                        </div>
                    )}
                    <Button id="cancel" type="button" content="Cancel" onClick={onCancel} />
                </div>
            </div>
        </div>
    );
};

SelfEmployedUploader.propTypes = {
    isOpenUploader: PropTypes.bool,
    onCancel: PropTypes.func,
    onExit: PropTypes.func
};

export default SelfEmployedUploader;
